import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';
import Button from '../components/ui/Button';
import { Input, Label } from '../components/ui/Input';
import DataGrid from '../components/grid/DataGrid';

// Commercial Terms Master pages (migrations 043/044) — the four COMMERCIALMASTER.xlsx
// sheets now governed in magic SQL. One config-driven grid, four sidebar pages.
// Browse + inline edit (double-click a non-key cell) + Export / Import — all through
// the same governed /commercial-master/{entity}/import path (audit + temporal history).
// Bulk / arbitrary-layout uploads still live in Master Data AI (AI column mapping).

const fmtDate = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime()) ? String(s) : d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};
const norm = (h) => String(h).toLowerCase().replace(/[^a-z0-9]/g, '');

// Display-only formatters (valueFormatter — never touch the stored/edited value):
// `pctFrac` renders a stored fraction (0.015) as a percent (1.5%); `pct` a value
// already on a 0–100 scale (25 -> 25%); `money` a dollar amount; `days` a lead time.
// Editing still edits the raw stored number, so the POST is byte-for-byte unchanged.
const fmtPctFrac = (v) => (v == null || v === '' ? '' : `${+(Number(v) * 100).toFixed(4)}%`);
const fmtPct = (v) => (v == null || v === '' ? '' : `${+Number(v).toFixed(2)}%`);
const fmtMoney = (v) => (v == null || v === '' ? '' : `$${Number(v).toFixed(2)}`);

// Each column: key, label, type ('s'|'number'|'date'), width|flex. keyCols = natural key (not editable).
const CFG = {
  'rebate-master': {
    label: 'Rebate Master', noun: 'rebates', keyCols: ['customerCode'],
    columns: [
      { key: 'customerCode', label: 'Customer Code', width: 170 },
      { key: 'rebatePct', label: 'Rebate %', type: 'number', fmt: 'pctFrac', width: 160 },
    ],
  },
  'growth-grid': {
    // Rolling-plan growth grid (migration 047). GrowthPct is type:'number' (plain,
    // like rebatePct — no percent formatter exists to mirror); an emptied cell → null
    // via cellDataType:'number', preserving blank(NULL)-vs-zero. Rationale is a default
    // (typeless) editable string. FiscalYear is a keyCol (editing it is a rename).
    label: 'Growth Grid', noun: 'growth rows', keyCols: ['category', 'fiscalYear'],
    columns: [
      { key: 'category', label: 'Category', width: 240, flex: 1 },
      { key: 'fiscalYear', label: 'Fiscal Year', type: 'number', width: 120 },
      { key: 'growthPct', label: 'Growth %', type: 'number', width: 140 },
      { key: 'rationale', label: 'Rationale', flex: 1, width: 320 },
    ],
  },
  'transport-allocation': {
    label: 'Transport Allocation', noun: 'allocation rows', keyCols: ['customerCode', 'dcId'],
    // Customer + DC are locked in the grid (still set in Add row): an in-grid edit renamed
    // the Port of Brisbane lane onto the wrong customer. Move a lane = delete + add.
    columns: [
      { key: 'customerCode', label: 'Customer', width: 200, flex: 1, lockInGrid: true },
      { key: 'dcId', label: 'DC', width: 260, dcLookup: true, lockInGrid: true },
      { key: 'shipToCustomerCode', label: 'Ship-To Code', width: 130 },
      { key: 'leadTimeDays', label: 'Lead Time', type: 'number', fmt: 'days', width: 110 },
      { key: 'allocationPct', label: 'Allocation', type: 'number', fmt: 'pct', width: 110 },
      { key: 'customerName', label: 'Customer Name', width: 160 },
    ],
  },
  'customer-dc': {
    label: 'Customer DCs', noun: 'DCs', keyCols: ['dcId'], noImport: true,
    columns: [
      { key: 'owner', label: 'Owner', width: 180, flex: 1 },
      { key: 'dc', label: 'DC', width: 200 },
      { key: 'state', label: 'State', width: 90 },
      { key: 'dcCode', label: 'DC Code', width: 130 },
      { key: 'warehouse', label: 'WH', width: 90 },
      { key: 'demandCustomers', label: 'Demand custs', type: 'number', width: 130, readonly: true },
    ],
  },
  'rrp-items': {
    label: 'RRP Items', noun: 'items', keyCols: ['customerCode', 'itemNumber'],
    columns: [
      { key: 'customerCode', label: 'Customer Code', width: 150 },
      { key: 'itemNumber', label: 'Item Number', width: 150 },
      { key: 'brand', label: 'Brand', flex: 1, width: 200 },
    ],
  },
  'promo-rrp': {
    label: 'Promo RRP', noun: 'promo windows', keyCols: ['customerCode', 'brand', 'promoType', 'fromDate'],
    columns: [
      { key: 'customerCode', label: 'Customer Code', width: 130 },
      { key: 'brand', label: 'Brand', width: 140 },
      { key: 'promoType', label: 'Promo Type', width: 150 },
      { key: 'fromDate', label: 'From Date', type: 'date', width: 130 },
      { key: 'toDate', label: 'To Date', type: 'date', width: 130 },
      { key: 'rrPrice', label: 'RRP', type: 'number', fmt: 'money', width: 100 },
      { key: 'redemption', label: 'Redemption', type: 'number', fmt: 'pctFrac', width: 120 },
      { key: 'rrPrice2', label: 'RRP 2', type: 'number', fmt: 'money', width: 100 },
    ],
  },
};

const CHIP = { display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, border: '1px solid var(--border-soft)', color: 'var(--text-muted)' };

// Cell editor for the Customer Code column: pick a customer by NAME (type the first
// letters to jump — native <select> type-ahead); the stored value is the code.
const CustomerCellEditor = forwardRef(function CustomerCellEditor(props, ref) {
  const [code, setCode] = useState(props.value ?? '');
  const selRef = useRef(null);
  useImperativeHandle(ref, () => ({ getValue: () => code || null }));
  useEffect(() => { selRef.current?.focus(); }, []);
  const list = props.customers ?? [];
  return (
    <select
      ref={selRef}
      value={code ?? ''}
      onChange={(e) => setCode(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') props.stopEditing?.(false); }}
      style={{ width: '100%', minWidth: 260, height: 30, border: '1px solid var(--border)', borderRadius: 4, padding: '0 6px', font: 'inherit', background: 'var(--bg-card)', color: 'var(--text-primary)' }}
    >
      <option value="">— select customer —</option>
      {list.map((c) => <option key={c.code} value={c.code}>{c.name} ({c.code})</option>)}
    </select>
  );
});

// Cell editor for the DC column: pick a DC by name; the stored value is its DcId.
const DcCellEditor = forwardRef(function DcCellEditor(props, ref) {
  const [id, setId] = useState(props.value == null ? '' : String(props.value));
  const selRef = useRef(null);
  useImperativeHandle(ref, () => ({ getValue: () => (id === '' ? null : Number(id)) }));
  useEffect(() => { selRef.current?.focus(); }, []);
  const list = props.dcs ?? [];
  return (
    <select
      ref={selRef}
      value={id}
      onChange={(e) => setId(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') props.stopEditing?.(false); }}
      style={{ width: '100%', minWidth: 280, height: 30, border: '1px solid var(--border)', borderRadius: 4, padding: '0 6px', font: 'inherit', background: 'var(--bg-card)', color: 'var(--text-primary)' }}
    >
      <option value="">— select DC —</option>
      {list.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
    </select>
  );
});

function CommercialMasterGrid({ entityKey }) {
  const cfg = CFG[entityKey];
  const apiFetch = useApi();
  const qc = useQueryClient();
  const fileRef = useRef(null);

  const [q, setQ] = useState('');
  const [gridApi, setGridApi] = useState(null);
  const [displayed, setDisplayed] = useState(0);
  const [gridError, setGridError] = useState(null);
  const [importState, setImportState] = useState(null);
  const [adding, setAdding] = useState(false);

  const route = `/commercial-master/${entityKey}`;

  const { data, isLoading, error } = useQuery({
    queryKey: ['commercial-master', entityKey, q],
    queryFn: () => {
      const sp = new URLSearchParams();
      if (q.trim()) sp.set('q', q.trim());
      return apiFetch(`${route}?${sp.toString()}`).then((r) => { if (!r.ok) throw new Error(`API ${r.status}`); return r.json(); });
    },
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const rows = data?.rows ?? [];

  // Customer master for the Customer Code lookup (select by name → store the code).
  const { data: custData } = useQuery({
    queryKey: ['master-customers-lookup'],
    queryFn: () => apiFetch('/master-customers?all=true').then((r) => (r.ok ? r.json() : [])),
    staleTime: 300_000,
  });
  const customers = useMemo(
    () => (Array.isArray(custData) ? custData : [])
      .map((c) => ({ code: c.customerCode, name: c.name || c.customerCode }))
      .filter((c) => c.code)
      .sort((a, b) => a.name.localeCompare(b.name)),
    [custData],
  );
  const nameByCode = useMemo(() => Object.fromEntries(customers.map((c) => [c.code, c.name])), [customers]);

  // DC master for the DC lookup on the allocation grid (select a DC, store its DcId).
  const { data: dcData } = useQuery({
    queryKey: ['customer-dc-lookup'],
    queryFn: () => apiFetch('/commercial-master/customer-dc').then((r) => (r.ok ? r.json() : { rows: [] })),
    staleTime: 300_000,
  });
  const dcs = useMemo(
    () => ((dcData?.rows) ?? [])
      .map((d) => ({ id: d.dcId, label: `${d.owner ? d.owner + ' — ' : ''}${d.dc}${d.state ? ' (' + d.state + ')' : ''}` }))
      .filter((d) => d.id != null)
      .sort((a, b) => a.label.localeCompare(b.label)),
    [dcData],
  );
  const dcLabelById = useMemo(() => Object.fromEntries(dcs.map((d) => [d.id, d.label])), [dcs]);

  // Delete a row (item 7). Confirm, POST /delete, surface a 409 (e.g. a DC still
  // referenced by transport lanes) inline, then refresh.
  const deleteRow = useCallback(async (dataRow) => {
    const lbl = cfg.keyCols.map((k) => dataRow[k]).join(' / ');
    if (!window.confirm(`Delete ${lbl}?`)) return;
    const key = {};
    for (const k of cfg.keyCols) key[k] = dataRow[k];
    try {
      const res = await apiFetch(`${route}/delete`, { method: 'POST', body: JSON.stringify({ key }) });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(result?.error ?? `Delete failed (${res.status})`);
      setGridError(null);
      qc.invalidateQueries({ queryKey: ['commercial-master', entityKey] });
    } catch (err) {
      setGridError(`${lbl}: ${String(err.message ?? err)}`);
    }
  }, [apiFetch, entityKey, route, qc, cfg]);

  // Add a row (item 7). AddRowModal collects the fields; the server INSERTs (identity
  // key) or upserts (natural key) and rejects a duplicate key / missing required field.
  const submitAdd = useCallback(async (row) => {
    const res = await apiFetch(`${route}/add`, { method: 'POST', body: JSON.stringify({ row }) });
    const result = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(result?.error ?? `Add failed (${res.status})`);
    qc.invalidateQueries({ queryKey: ['commercial-master', entityKey] });
  }, [apiFetch, entityKey, route, qc]);

  const columns = useMemo(() => [...cfg.columns.map((c) => ({
    headerName: c.label,
    field: c.key,
    editable: !c.readonly && !c.lockInGrid,   // lockInGrid: not editable here, still in Add row; key edits become a rename
    ...(c.flex ? { flex: c.flex, minWidth: c.width } : { width: c.width }),
    ...(c.fmt === 'pctFrac' ? { cellDataType: 'number', valueFormatter: (p) => fmtPctFrac(p.value) }
      : c.fmt === 'pct' ? { cellDataType: 'number', valueFormatter: (p) => fmtPct(p.value) }
      : c.fmt === 'money' ? { cellDataType: 'number', valueFormatter: (p) => fmtMoney(p.value) }
      : c.fmt === 'days' ? { cellDataType: 'number', valueFormatter: (p) => (p.value == null || p.value === '' ? '' : `${p.value} d`) }
      : c.type === 'number' ? { cellDataType: 'number' } : {}),
    ...(c.type === 'date' ? { filter: false, valueFormatter: (p) => fmtDate(p.value) } : {}),
    // Customer Code = pick a customer by name; the code is stored. Cell shows "Name (CODE)".
    ...(c.key === 'customerCode' ? {
      cellEditor: CustomerCellEditor,
      cellEditorParams: { customers },
      cellEditorPopup: true,
      valueFormatter: (p) => (p.value ? (nameByCode[p.value] ? `${nameByCode[p.value]} (${p.value})` : p.value) : ''),
      filterValueGetter: (p) => { const code = p.data?.customerCode; return code ? `${nameByCode[code] ?? ''} ${code}` : ''; },
    } : {}),
    // DC = pick a DC by name; the DcId is stored. Cell shows the DC's "Owner — DC (State)".
    ...(c.dcLookup ? {
      cellEditor: DcCellEditor,
      cellEditorParams: { dcs },
      cellEditorPopup: true,
      valueFormatter: (p) => (p.value != null ? (dcLabelById[p.value] ?? `#${p.value}`) : ''),
      filterValueGetter: (p) => dcLabelById[p.data?.dcId] ?? '',
    } : {}),
  })), {
    headerName: '', colId: '__del', width: 54, pinned: 'right',
    editable: false, sortable: false, filter: false, resizable: false, suppressMovable: true,
    cellRenderer: DeleteCell, cellRendererParams: { onDelete: deleteRow },
  }], [entityKey, customers, nameByCode, dcs, dcLabelById, deleteRow]);

  const onGridReady = useCallback((e) => setGridApi(e.api), []);
  const updateDisplayed = useCallback((e) => setDisplayed(e.api.getDisplayedRowCount()), []);
  const getRowId = useCallback((p) => cfg.keyCols.map((k) => p.data[k]).join('|'), [entityKey]);
  const keyLabel = (d) => cfg.keyCols.map((k) => d[k]).join(' / ');

  // Inline edit of ANY field (incl. key columns). The old key locates the row;
  // a key edit becomes a rename server-side. Revert the cell on failure.
  const onCellValueChanged = useCallback(async (e) => {
    const field = e.colDef.field;
    if (!field || e.newValue === e.oldValue) return;
    // Editing a key column is a rename — confirm before sending confirmRename (item 9).
    const isRename = cfg.keyCols.includes(field);
    if (isRename && !window.confirm(`Rename ${field} "${e.oldValue}" → "${e.newValue}"? This moves the row and its history.`)) {
      e.node.setDataValue(field, e.oldValue);
      return;
    }
    const oldKey = {};
    for (const k of cfg.keyCols) oldKey[k] = (k === field) ? e.oldValue : e.data[k];
    const row = { ...e.data };   // all current values, including the applied edit
    try {
      const res = await apiFetch(`${route}/update`, { method: 'POST', body: JSON.stringify({ oldKey, row, confirmRename: isRename }) });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(result?.error ?? `Save failed (${res.status})`);
      setGridError(null);
    } catch (err) {
      setGridError(`${keyLabel(e.data)}: ${String(err.message ?? err)}`);
      e.node.setDataValue(field, e.oldValue);
    }
  }, [apiFetch, entityKey]);

  async function exportExcel() {
    const XLSX = await import('xlsx');
    const out = [];
    if (gridApi) gridApi.forEachNodeAfterFilterAndSort((n) => { if (n.data) out.push(n.data); });
    else out.push(...rows);
    const sheet = out.map((r) => Object.fromEntries(cfg.columns.map((c) => [c.label, r[c.key] ?? ''])));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet), cfg.label.slice(0, 31));
    XLSX.writeFile(wb, `${cfg.label.replace(/\s/g, '')}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  async function onImportFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const XLSX = await import('xlsx');
    const wb = XLSX.read(await file.arrayBuffer(), { cellDates: true });   // native dates as Date, not serials
    const raw = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
    const KEYMAP = {};
    for (const c of cfg.columns) { KEYMAP[norm(c.label)] = c.key; KEYMAP[norm(c.key)] = c.key; }
    const mapped = raw.map((r) => {
      const o = {};
      for (const [h, v] of Object.entries(r)) { const k = KEYMAP[norm(h)]; if (k && v !== '') o[k] = v; }
      return o;
    }).filter((r) => cfg.keyCols.every((k) => r[k] !== undefined && r[k] !== ''));
    if (!mapped.length) {
      setImportState({ fileName: file.name, error: `No rows with the key columns (${cfg.keyCols.join(', ')}) found. For arbitrary layouts use Master Data AI.` });
      return;
    }
    setImportState({ fileName: file.name, rows: mapped });
    try {
      const res = await apiFetch(`${route}/import`, { method: 'POST', body: JSON.stringify({ rows: mapped, dryRun: true, fileName: file.name }) });
      const preview = await res.json();
      if (!res.ok) throw new Error(preview?.error ?? `Preview failed (${res.status})`);
      setImportState((s) => ({ ...s, preview }));
    } catch (err) { setImportState((s) => ({ ...s, error: String(err.message ?? err) })); }
  }

  async function commitImport() {
    setImportState((s) => ({ ...s, committing: true }));
    try {
      const res = await apiFetch(`${route}/import`, { method: 'POST', body: JSON.stringify({ rows: importState.rows, dryRun: false, fileName: importState.fileName }) });
      const result = await res.json();
      if (!res.ok) throw new Error(result?.error ?? `Import failed (${res.status})`);
      qc.invalidateQueries({ queryKey: ['commercial-master', entityKey] });
      setImportState((s) => ({ ...s, committing: false, result }));
    } catch (err) { setImportState((s) => ({ ...s, committing: false, error: String(err.message ?? err) })); }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '7px 11px', display: 'flex', gap: 9, alignItems: 'center' }}>
        <span style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '1px 6px', borderRadius: 999, border: '1px solid var(--border)', whiteSpace: 'nowrap' }}>Live</span>
        <span>This is the <b>live source for the demand plan</b> (COMMERCIALMASTER.xlsx retired 9 Oct 2026). Changes reach the forecast at its next refresh. Governed: audited, with full history.</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={CHIP}>{(data?.total ?? rows.length).toLocaleString()} {cfg.noun}</span>
        <span style={{ flex: 1 }} />
        <Button size="sm" onClick={() => setAdding(true)}>Add row</Button>
        {!cfg.noImport && <Button size="sm" onClick={() => fileRef.current?.click()}>Import Excel</Button>}
        <Button size="sm" variant="primary" onClick={exportExcel}>Export ({displayed.toLocaleString()})</Button>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={onImportFile} />
      </div>

      <div style={{ width: 320, maxWidth: '100%' }}>
        <Label>Search</Label>
        <Input placeholder={`Search ${cfg.noun}…`} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {error && <div style={{ fontSize: 12, color: 'var(--danger)' }}>Failed to load {cfg.label}: {String(error.message ?? error)}</div>}
      {gridError && <div style={{ fontSize: 12, color: 'var(--danger)' }}>{gridError}</div>}

      <DataGrid
        rowData={rows}
        columnDefs={columns}
        onGridReady={onGridReady}
        onCellValueChanged={onCellValueChanged}
        onModelUpdated={updateDisplayed}
        getRowId={getRowId}
        loading={isLoading}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: 'var(--text-muted)' }}>
        <span>{displayed.toLocaleString()} {cfg.noun}</span>
        <span style={{ flex: 1 }} />
        <span>Filter any column header · double-click any cell to edit (governed + audited) · {cfg.columns.some((c) => c.lockInGrid)
          ? 'customer and DC are locked — to move a row, delete it and add a new one'
          : 'editing a key renames the row'}</span>
      </div>

      {importState && <ImportModal label={cfg.label} state={importState} onCommit={commitImport} onClose={() => setImportState(null)} />}
      {adding && <AddRowModal cfg={cfg} customers={customers} dcs={dcs} onSubmit={submitAdd} onClose={() => setAdding(false)} />}
    </div>
  );
}

// Trash-button cell renderer for the pinned actions column (item 7).
function DeleteCell(props) {
  if (!props.data) return null;
  return (
    <button
      title="Delete row"
      onClick={(e) => { e.stopPropagation(); props.onDelete?.(props.data); }}
      style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--danger)', fontSize: 14, lineHeight: 1, padding: 0 }}
    >✕</button>
  );
}

// Add-a-row modal (item 7). One input per non-readonly column; customer/DC columns
// reuse the same name-select as the grid. Submits { row } to /add; the server
// rejects a duplicate key or a missing required field with an inline message.
function AddRowModal({ cfg, customers, dcs, onSubmit, onClose }) {
  const editable = cfg.columns.filter((c) => !c.readonly);
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const sel = { width: '100%', height: 32, border: '1px solid var(--border)', borderRadius: 4, padding: '0 6px', font: 'inherit', background: 'var(--bg-card)', color: 'var(--text-primary)' };
  const submit = async () => {
    setSubmitting(true); setError(null);
    try { await onSubmit(form); onClose(); }
    catch (err) { setError(String(err.message ?? err)); setSubmitting(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} onClick={onClose} />
      <div style={{ position: 'relative', width: 'min(460px, 94vw)', maxHeight: '84vh', overflow: 'auto', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Add {cfg.label}</div>
        {editable.map((c) => (
          <div key={c.key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Label>{c.label}</Label>
            {c.key === 'customerCode'
              ? (
                <select value={form[c.key] ?? ''} onChange={(e) => set(c.key, e.target.value)} style={sel}>
                  <option value="">— select customer —</option>
                  {customers.map((x) => <option key={x.code} value={x.code}>{x.name} ({x.code})</option>)}
                </select>
              ) : c.dcLookup ? (
                <select value={form[c.key] ?? ''} onChange={(e) => set(c.key, e.target.value === '' ? '' : Number(e.target.value))} style={sel}>
                  <option value="">— select DC —</option>
                  {dcs.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                </select>
              ) : c.type === 'date' ? (
                <Input type="date" value={form[c.key] ?? ''} onChange={(e) => set(c.key, e.target.value)} />
              ) : c.type === 'number' ? (
                <Input type="number" value={form[c.key] ?? ''} onChange={(e) => set(c.key, e.target.value)} />
              ) : (
                <Input value={form[c.key] ?? ''} onChange={(e) => set(c.key, e.target.value)} />
              )}
          </div>
        ))}
        {error && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <Button size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" variant="primary" disabled={submitting} onClick={submit}>{submitting ? 'Adding…' : 'Add'}</Button>
        </div>
      </div>
    </div>
  );
}

function ImportModal({ label, state, onCommit, onClose }) {
  const { fileName, preview, committing, result, error } = state;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} onClick={onClose} />
      <div style={{ position: 'relative', width: 'min(560px, 94vw)', maxHeight: '84vh', overflow: 'auto', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{label} import — {fileName}</div>
        {error && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</div>}
        {!error && !preview && !result && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Analysing file…</div>}
        {!error && preview && !result && (<>
          <div style={{ fontSize: 13, color: 'var(--text-body)' }}>
            {preview.total.toLocaleString()} rows · <b>{preview.adds}</b> new · <b>{preview.updates}</b> updates · {preview.errors.length} errors{preview.warnings?.length ? ` · ${preview.warnings.length} warnings` : ''}
            {preview.errors.length > 0 && <span style={{ color: 'var(--text-muted)' }}> (error rows are skipped)</span>}
          </div>
          {preview.errors.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 18, maxHeight: 120, overflow: 'auto', fontSize: 12, color: 'var(--danger)' }}>
              {preview.errors.slice(0, 25).map((e2, i) => <li key={i}>Row {e2.row}: {e2.error}</li>)}
            </ul>
          )}
          {preview.warnings?.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 18, maxHeight: 100, overflow: 'auto', fontSize: 12, color: 'var(--warn-text)' }}>
              {preview.warnings.slice(0, 25).map((w, i) => <li key={i}>⚠ Row {w.row}: {w.warning}</li>)}
            </ul>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" variant="primary" disabled={committing || (preview.adds === 0 && preview.updates === 0)} onClick={onCommit}>
              {committing ? 'Importing…' : 'Apply import'}
            </Button>
          </div>
        </>)}
        {result && (<>
          <div style={{ fontSize: 13, color: 'var(--text-body)' }}>Done — {result.adds} new, {result.updates} updated, {result.errors.length} errors.</div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}><Button size="sm" variant="primary" onClick={onClose}>Close</Button></div>
        </>)}
      </div>
    </div>
  );
}

// Fiscal years shown as columns in the growth-grid matrix (mirrors the seed FY2028–2031).
const GROWTH_FYS = [2028, 2029, 2030, 2031];

// Rolling-plan growth grid as an intuitive MATRIX: one row per category, one column per
// fiscal year, the year-on-year growth % editable per cell. Same governed store as the flat
// view — each cell edit PUTs the (category, fiscalYear) row through .../update (audit +
// temporal history). Blank-vs-zero preserved: an emptied cell = NULL (no decision), typing 0
// = a decided 0%. Values are FRACTIONAL in SQL (5% = 0.05); shown and entered as a percent.
function GrowthGridMatrix() {
  const apiFetch = useApi();
  const qc = useQueryClient();
  const [gridError, setGridError] = useState(null);
  const route = '/commercial-master/growth-grid';

  const { data, isLoading, error } = useQuery({
    queryKey: ['commercial-master', 'growth-grid'],
    queryFn: () => apiFetch(route).then((r) => { if (!r.ok) throw new Error(`API ${r.status}`); return r.json(); }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const rows = useMemo(() => data?.rows ?? [], [data]);

  // original rows indexed by "category|fiscalYear" — preserves Rationale when only the % is edited
  const byKey = useMemo(() => {
    const m = {};
    for (const r of rows) m[`${r.category}|${r.fiscalYear}`] = r;
    return m;
  }, [rows]);

  // pivot: one row per category, a fyNNNN field per year (value may be null = undecided)
  const pivotRows = useMemo(() => {
    const cats = [...new Set(rows.map((r) => r.category))].sort((a, b) => String(a).localeCompare(String(b)));
    return cats.map((cat) => {
      const row = { category: cat };
      for (const fy of GROWTH_FYS) { const src = byKey[`${cat}|${fy}`]; row[`fy${fy}`] = src ? src.growthPct : null; }
      return row;
    });
  }, [rows, byKey]);

  const columns = useMemo(() => [
    { headerName: 'Category', field: 'category', pinned: 'left', editable: false, width: 250, cellStyle: { fontWeight: 600 } },
    ...GROWTH_FYS.map((fy) => ({
      headerName: `FY${fy}`, field: `fy${fy}`, editable: true, width: 130, filter: false, sortable: false,
      type: 'rightAligned', cellDataType: false,
      valueFormatter: (p) => (p.value == null ? '' : `${(p.value * 100).toLocaleString('en-AU', { maximumFractionDigits: 2 })}%`),
      valueParser: (p) => {
        const raw = p.newValue;
        if (raw == null || String(raw).trim() === '') return null;   // blank = no decision (NULL)
        const n = Number(String(raw).replace('%', '').trim());
        return Number.isFinite(n) ? n / 100 : p.oldValue;            // "5" or "5%" -> 0.05
      },
      cellStyle: (p) => (p.value == null ? { color: 'var(--text-muted)', fontStyle: 'italic' } : null),
    })),
  ], []);

  const getRowId = useCallback((p) => String(p.data.category), []);

  // Edit a % cell -> update the (category, fiscalYear) row; Rationale carried over untouched.
  const onCellValueChanged = useCallback(async (e) => {
    const field = e.colDef.field;
    if (!field || !field.startsWith('fy') || e.newValue === e.oldValue) return;
    const fiscalYear = Number(field.slice(2));
    const category = e.data.category;
    const existing = byKey[`${category}|${fiscalYear}`] ?? {};
    const oldKey = { category, fiscalYear };
    const row = { category, fiscalYear, growthPct: e.newValue ?? null, rationale: existing.rationale ?? null };
    try {
      const res = await apiFetch(`${route}/update`, { method: 'POST', body: JSON.stringify({ oldKey, row }) });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(result?.error ?? `Save failed (${res.status})`);
      setGridError(null);
      qc.invalidateQueries({ queryKey: ['commercial-master', 'growth-grid'] });
    } catch (err) {
      setGridError(`${category} FY${fiscalYear}: ${String(err.message ?? err)}`);
      e.node.setDataValue(field, e.oldValue);
    }
  }, [apiFetch, byKey, qc]);

  const catCount = useMemo(() => new Set(rows.map((r) => r.category)).size, [rows]);
  const decided = useMemo(() => rows.filter((r) => r.growthPct != null).length, [rows]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 12, color: 'var(--warn-text)', background: 'var(--warn-soft)', border: '1px solid var(--warn-border)', borderRadius: 'var(--radius)', padding: '7px 11px', display: 'flex', gap: 9, alignItems: 'center' }}>
        <span style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', padding: '1px 6px', borderRadius: 999, border: '1px solid var(--warn-border)', whiteSpace: 'nowrap' }}>Preview</span>
        <span>Governed in SQL with full audit &amp; history — but <b>not yet feeding the demand plan</b>. The Excel model stays the live source until cutover.</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-muted)' }}>
        <span style={CHIP}>{catCount} categories × {GROWTH_FYS.length} years</span>
        <span style={CHIP}>{decided}/{rows.length} decided</span>
        <span style={{ flex: 1 }} />
        <span>Year-on-year growth · double-click a cell, type <b>5</b> for 5% · <i>empty = no decision</i>, type <b>0</b> for a deliberate 0%</span>
      </div>

      {error && <div style={{ fontSize: 12, color: 'var(--danger)' }}>Failed to load Growth Grid: {String(error.message ?? error)}</div>}
      {gridError && <div style={{ fontSize: 12, color: 'var(--danger)' }}>{gridError}</div>}

      <DataGrid height={720} rowData={pivotRows} columnDefs={columns} onCellValueChanged={onCellValueChanged} getRowId={getRowId} loading={isLoading} />
    </div>
  );
}

export function RebateMaster() { return <CommercialMasterGrid entityKey="rebate-master" />; }
export function GrowthGrid() { return <GrowthGridMatrix />; }
export function TransportAllocation() { return <CommercialMasterGrid entityKey="transport-allocation" />; }
export function RrpItems() { return <CommercialMasterGrid entityKey="rrp-items" />; }
export function PromoRrp() { return <CommercialMasterGrid entityKey="promo-rrp" />; }
export function CustomerDC() { return <CommercialMasterGrid entityKey="customer-dc" />; }
